// Firebase Web SDK — loaded with a DYNAMIC import inside getWebPushToken().
//
// `firebase/app` + `firebase/messaging` (+ their `util` / `installations`
// transitive deps) are ~234 KB of source, ~9% of the entry chunk. They were
// landing in the startup path because this module is reached eagerly:
//   native-lifecycle.tsx → use-web-push.ts → this file
// so every cold start parsed the whole Firebase SDK before first paint, even
// though a push token is only ever requested *after* the app is up and the
// user has granted notification permission. `import type` below is erased at
// build time, so the types cost nothing.
import type { FirebaseOptions } from "firebase/app";

const FIREBASE_CONFIG_KEYS = [
  "VITE_FIREBASE_API_KEY",
  "VITE_FIREBASE_AUTH_DOMAIN",
  "VITE_FIREBASE_PROJECT_ID",
  "VITE_FIREBASE_STORAGE_BUCKET",
  "VITE_FIREBASE_MESSAGING_SENDER_ID",
  "VITE_FIREBASE_APP_ID",
] as const;

function firebaseConfig(): FirebaseOptions {
  const missing = FIREBASE_CONFIG_KEYS.filter((key) => !import.meta.env[key]);
  if (missing.length > 0) {
    throw new Error(`Missing Firebase web config: ${missing.join(", ")}`);
  }

  return {
    apiKey: import.meta.env["VITE_FIREBASE_API_KEY"],
    authDomain: import.meta.env["VITE_FIREBASE_AUTH_DOMAIN"],
    projectId: import.meta.env["VITE_FIREBASE_PROJECT_ID"],
    storageBucket: import.meta.env["VITE_FIREBASE_STORAGE_BUCKET"],
    messagingSenderId: import.meta.env["VITE_FIREBASE_MESSAGING_SENDER_ID"],
    appId: import.meta.env["VITE_FIREBASE_APP_ID"],
  };
}

function serviceWorkerUrl(config: FirebaseOptions): string {
  const params = new URLSearchParams({
    apiKey: String(config.apiKey),
    authDomain: String(config.authDomain),
    projectId: String(config.projectId),
    storageBucket: String(config.storageBucket),
    messagingSenderId: String(config.messagingSenderId),
    appId: String(config.appId),
  });
  return `/firebase-messaging-sw.js?${params.toString()}`;
}

export async function getWebPushToken(): Promise<string> {
  if (!("serviceWorker" in navigator)) {
    throw new Error("Service workers are not supported in this browser.");
  }

  // Check the cheap preconditions BEFORE pulling the SDK over the wire, so an
  // unsupported browser or a misconfigured build never downloads 234 KB to
  // find that out.
  const vapidKey = import.meta.env["VITE_FIREBASE_VAPID_KEY"];
  if (!vapidKey) {
    throw new Error("Missing VITE_FIREBASE_VAPID_KEY.");
  }

  const [{ initializeApp, getApps }, { getMessaging, getToken, isSupported }] = await Promise.all([
    import("firebase/app"),
    import("firebase/messaging"),
  ]);

  if (!(await isSupported())) {
    throw new Error("Firebase messaging is not supported in this browser.");
  }

  const config = firebaseConfig();
  const app = getApps().length > 0 ? getApps()[0]! : initializeApp(config);
  const registration = await navigator.serviceWorker.register(serviceWorkerUrl(config));
  const token = await getToken(getMessaging(app), {
    vapidKey,
    serviceWorkerRegistration: registration,
  });

  if (!token) {
    throw new Error("Firebase did not return a web push token.");
  }
  return token;
}
